// Grid shapes made of several grids of one size (item 13, the approved mockups; smaller grids
// too, Marko 2026-11-04; core/src/doc.rs shape_layout): where each grid's top left corner sits
// on the canvas. Boxes two grids share are one box.
import type { PuzzleDoc, Rc } from './puzzle';

export type ShapeKey = 'samurai' | 'twodoku' | 'butterfly' | 'windmill' | 'sohei';

export interface Shape {
  key: ShapeKey;
  name: string;
  /** The grid sizes it comes in. */
  sizes: readonly number[];
  /** What it is, under the Shape row, for grids of `n`×`n`. */
  note: (n: number) => string;
}

const g = (n: number) => `${n}×${n}`;

export const SHAPES: readonly Shape[] = [
  {
    key: 'samurai',
    name: 'Samurai',
    sizes: [4, 6, 8, 9],
    note: (n) => `A samurai is five ${g(n)} grids that share their corner boxes.`,
  },
  {
    key: 'twodoku',
    name: 'Twodoku',
    sizes: [4, 6, 8, 9],
    note: (n) => `A twodoku is two ${g(n)} grids that share a box.`,
  },
  {
    key: 'butterfly',
    name: 'Butterfly',
    sizes: [4, 6, 8, 9],
    note: (n) => `A butterfly is four ${g(n)} grids, each overlapping the others.`,
  },
  {
    key: 'windmill',
    name: 'Windmill',
    sizes: [9],
    note: (n) =>
      `A windmill is four ${g(n)} grids round a centre one, each sharing two boxes with it.`,
  },
  {
    key: 'sohei',
    name: 'Sohei',
    sizes: [4, 6, 8, 9],
    note: (n) => `A sohei is four ${g(n)} grids in a ring, each sharing a box with two others.`,
  },
];

export function shapeOf(key: string | null | undefined): Shape | undefined {
  return SHAPES.find((s) => s.key === key);
}

/** Box shape of an n×n grid, as the core picks it (`doc::default_boxes`). */
function boxesOf(n: number): [number, number] {
  let best: [number, number] = [0, 0];
  for (let r = 2; r * r <= n; r++) if (n % r === 0) best = [r, n / r];
  return best;
}

/** The canvas and each grid's top left corner for a shape of `n`×`n` grids (as
 * `doc::shape_layout`); null for a size it doesn't come in. */
export function shapeLayout(
  key: ShapeKey,
  n: number,
): { rows: number; cols: number; corners: [number, number][] } | null {
  const [br, bc] = boxesOf(n);
  if (br === 0 || n < 4 || n > 9 || !shapeOf(key)?.sizes.includes(n)) return null;
  const [dr, dc] = [n - br, n - bc];
  const corners: [number, number][] =
    key === 'samurai'
      ? [
          [0, 0],
          [0, 2 * dc],
          [dr, dc],
          [2 * dr, 0],
          [2 * dr, 2 * dc],
        ]
      : key === 'twodoku'
        ? [
            [0, 0],
            [dr, dc],
          ]
        : key === 'butterfly'
          ? [
              [0, 0],
              [0, bc],
              [br, 0],
              [br, bc],
            ]
          : key === 'windmill'
            ? [
                [0, 3],
                [3, 12],
                [6, 6],
                [9, 0],
                [12, 9],
              ]
            : [
                [0, dc],
                [dr, 0],
                [dr, 2 * dc],
                [2 * dr, dc],
              ];
  return {
    rows: Math.max(...corners.map((c) => c[0])) + n,
    cols: Math.max(...corners.map((c) => c[1])) + n,
    corners,
  };
}

/** The grid of a shape of `n`×`n` grids, digits 1 to n, as the core builds it. */
export function shapedGrid(key: ShapeKey, n = 9): PuzzleDoc['grid'] {
  const layout = shapeLayout(key, n)!;
  const [br, bc] = boxesOf(n);
  const seen = new Set<string>();
  const cells: Rc[] = [];
  for (const [r0, c0] of layout.corners) {
    for (let r = r0; r < r0 + n; r++) {
      for (let c = c0; c < c0 + n; c++) {
        if (!seen.has(`${r},${c}`)) {
          seen.add(`${r},${c}`);
          cells.push([r, c]);
        }
      }
    }
  }
  cells.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  return {
    rows: layout.rows,
    cols: layout.cols,
    cells,
    boxRows: br,
    boxCols: bc,
    digits: Array.from({ length: n }, (_, i) => String(i + 1)),
    subGrids: layout.corners.map(([row, col]) => ({
      row,
      col,
      rows: n,
      cols: n,
      boxRows: br,
      boxCols: bc,
    })),
  };
}

/** The shape of a document's grid, when it is one of these. */
export function shapeOfGrid(grid: PuzzleDoc['grid']): ShapeKey | null {
  const subs = grid.subGrids ?? [];
  if (subs.length < 2) return null;
  const n = subs[0]!.rows;
  const found = SHAPES.find((s) => {
    const layout = shapeLayout(s.key, n);
    return (
      layout !== null &&
      layout.rows === grid.rows &&
      layout.cols === grid.cols &&
      layout.corners.length === subs.length &&
      layout.corners.every(([r, c]) => subs.some((x) => x.row === r && x.col === c))
    );
  });
  return found?.key ?? null;
}

/** The size of the grids a shaped document is made of. */
export function shapeGridSize(grid: PuzzleDoc['grid']): number | null {
  return grid.subGrids?.[0]?.rows ?? null;
}
